import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Archive, ArrowLeft, CheckCircle, Clock, Download, Euro, ExternalLink, FolderArchive, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate, formatMoney } from './constants';

export default function ArchivesTab({
  documents: documents,
  expandedIds,
  filtered,
  getAmount,
  getName,
  isPaid,
  setActiveTab,
  toggleExpand
}) {
  const archived = filtered.filter(d => d.archived || d.ged_archived);

  return (
    <>
      {/* KPIs compactes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Archive className="h-4 w-4 text-slate-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Archives</p>
            <p className="text-lg font-bold text-gray-900">{archived.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Payes & archives</p>
            <p className="text-lg font-bold text-emerald-700">{archived.filter(d => isPaid(d)).length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Euro className="h-4 w-4 text-blue-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Montant archive</p>
            <p className="text-lg font-bold text-blue-700">{formatMoney(archived.reduce((s, d) => s + getAmount(d), 0))} EUR</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <FolderArchive className="h-4 w-4 text-purple-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Stockes GED</p>
            <p className="text-lg font-bold text-purple-700">{archived.filter(d => d.ged_file_id).length}</p>
          </div>
        </div>
      </div>

      {/* Count */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{archived.length} document(s) archive(s)</span>
      </div>

      {/* Liste */}
      {archived.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Archive className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucun document archive</p>
          <p className="text-sm text-gray-400">Utilisez le bouton "Archiver GED" sur une souscription payee</p>
          <Button variant="outline" className="mt-4 gap-1.5" onClick={() => setActiveTab('paiements')}>
            <ArrowLeft className="h-4 w-4" /> Retour aux paiements
          </Button>
        </div>
      ) : (
        <div className="space-y-1" data-testid="archives-list">
          {archived.map((doc, idx) => {
            const arcId = `arc-${doc.reference}`;
            const isExpanded = expandedIds.has(arcId);
            return (
              <div
                key={arcId}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isExpanded ? "border-slate-300 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`archive-row-${idx}`}
              >
                {/* Compact row */}
                <button onClick={() => toggleExpand(arcId)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`archive-toggle-${idx}`}>
                  <Archive className={cn("h-4 w-4 shrink-0", isExpanded ? "text-slate-600" : "text-slate-400")} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-slate-700" : "text-gray-900")}>{getName(doc)}</p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{doc.reference}</p>
                  </div>
                  {doc.ged_file_id ? (
                    <Badge className="bg-emerald-700 text-white text-[10px] shrink-0 font-semibold"><CheckCircle className="h-3 w-3 mr-0.5" />Stocke GED</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-slate-600 shrink-0"><Clock className="h-3 w-3 mr-0.5" />En attente</Badge>
                  )}
                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    {doc.archived_at && <><span>{formatDate(doc.archived_at)}</span></>}
                  </div>
                  <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">{formatMoney(getAmount(doc))} EUR</span>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-slate-500")} />
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`archive-details-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Souscripteur</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{getName(doc)}</p>
                          <p className="text-gray-500 text-xs truncate">{doc.email || 'Email non renseigne'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between"><span className="text-gray-500">Reference:</span><span className="font-mono text-xs">{doc.reference}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Montant:</span><span className="font-bold text-gray-900">{formatMoney(getAmount(doc))} EUR</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Date archivage:</span><span className="text-gray-700">{doc.archived_at ? formatDate(doc.archived_at) : 'N/A'}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Statut GED:</span>
                            {doc.ged_file_id ? <Badge className="bg-emerald-700 text-white text-[10px] font-semibold">Stocke</Badge> : <Badge variant="outline" className="text-[10px]">En attente</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`archive-download-${idx}`}>
                            <Download className="h-3.5 w-3.5" /> Telecharger
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`archive-ged-${idx}`}>
                            <ExternalLink className="h-3.5 w-3.5" /> Voir GED
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
